import { mapValues } from 'lodash-es';
import { fullName } from '~/infra/utils';
import { relationshipSummaryModel } from '~/models';
const distinctNameOptions = (values)=>{
    const seen = new Set();
    values.forEach((value)=>{
        const trimmed = value === null || value === void 0 ? void 0 : value.trim();
        if (trimmed) seen.add(trimmed);
    });
    return [
        ...seen
    ].sort((a, b)=>a.localeCompare(b)).map((value)=>({
            value,
            label: value
        }));
};
export const deriveNameFilterOptions = (agreements, isVendor)=>({
        clientName: distinctNameOptions(agreements.map((a)=>relationshipSummaryModel.customerBusinessName(a))),
        agreementTitle: distinctNameOptions(agreements.map((a)=>relationshipSummaryModel.displayedTitle(a, isVendor))),
        contactName: distinctNameOptions(agreements.map((a)=>{
            var _ref, _ref1;
            var _a_buyer, _a_buyer1;
            return fullName((_ref = (_a_buyer = a.buyer) === null || _a_buyer === void 0 ? void 0 : _a_buyer.firstName) !== null && _ref !== void 0 ? _ref : null, (_ref1 = (_a_buyer1 = a.buyer) === null || _a_buyer1 === void 0 ? void 0 : _a_buyer1.lastName) !== null && _ref1 !== void 0 ? _ref1 : null);
        })),
        assignee: distinctNameOptions(agreements.map((a)=>relationshipSummaryModel.sellerFullName(a)))
    });
const sortByCompanyName = (isVendor)=>(a, b)=>{
        const nameA = isVendor ? a.customer.name.toLowerCase() : a.vendor.name.toLowerCase();
        const nameB = isVendor ? b.customer.name.toLowerCase() : b.vendor.name.toLowerCase();
        if (nameA < nameB) {
            return -1;
        }
        if (nameA > nameB) {
            return 1;
        }
        return 0;
    };
const sortById = (a, b)=>a.id < b.id ? -1 : 1;
const sortByEffectiveDate = (a, b)=>b.effectiveDate && a.effectiveDate && b.effectiveDate.getTime() - a.effectiveDate.getTime();
const sortByAssignee = (a, b)=>{
    const nameA = (relationshipSummaryModel.sellerFullName(a) || '').toLowerCase();
    const nameB = (relationshipSummaryModel.sellerFullName(b) || '').toLowerCase();
    if (nameA < nameB) {
        return -1;
    }
    if (nameA > nameB) {
        return 1;
    }
    return 0;
};
function sortByLastModified(a, b) {
    const aRecord = relationshipSummaryModel.lastModificationRecord(a);
    const bRecord = relationshipSummaryModel.lastModificationRecord(b);
    if (!aRecord && !bRecord) return 0;
    if (!aRecord) return 1;
    if (!bRecord) return -1;
    return new Date(bRecord.savedOn).getTime() - new Date(aRecord.savedOn).getTime();
}
export var HeaderColId = /*#__PURE__*/ function(HeaderColId) {
    HeaderColId["companyName"] = "companyName";
    HeaderColId["status"] = "status";
    HeaderColId["paymentMethod"] = "paymentMethod";
    HeaderColId["effectiveDate"] = "effectiveDate";
    HeaderColId["lastBilled"] = "lastBilled";
    HeaderColId["assignee"] = "assignee";
    HeaderColId["lastModifiedBy"] = "lastModifiedBy";
    return HeaderColId;
}({});
export const sortFunctions = {
    ["companyName"]: (param)=>{
        let { isAscending, isVendor } = param;
        return (a, b)=>{
            const retval = sortByCompanyName(isVendor)(a, b) || sortById(a, b);
            return isAscending ? -retval : retval;
        };
    },
    ["status"]: (param)=>{
        let { isAscending } = param;
        return (a, b)=>{
            const retval = sortByAgreementStatus(a, b) || sortById(a, b);
            return isAscending ? -retval : retval;
        };
    },
    ["lastBilled"]: (param)=>{
        let { isAscending } = param;
        return (a, b)=>{
            const retval = sortByLastBilledDate(a, b) || sortById(a, b);
            return isAscending ? -retval : retval;
        };
    },
    ["paymentMethod"]: (param)=>{
        let { isAscending } = param;
        return (a, b)=>{
            const retval = sortByPaymentMethod(a, b) || sortById(a, b);
            return isAscending ? -retval : retval;
        };
    },
    ["effectiveDate"]: (param)=>{
        let { isAscending } = param;
        return (a, b)=>{
            const retval = sortByEffectiveDate(a, b) || sortById(a, b);
            return isAscending ? -retval : retval;
        };
    },
    ["assignee"]: (param)=>{
        let { isAscending } = param;
        return (a, b)=>{
            const retval = sortByAssignee(a, b) || sortById(a, b);
            return isAscending ? -retval : retval;
        };
    },
    ["lastModifiedBy"]: (param)=>{
        let { isAscending } = param;
        return (a, b)=>{
            const retval = sortByLastModified(a, b) || sortById(a, b);
            return isAscending ? -retval : retval;
        };
    }
};
const AD_HOC_STATUS_SORT_ORDER = 1;
const agreementStatusSortOrder = {
    [relationshipSummaryModel.AgreementStatus.Active]: 2,
    [relationshipSummaryModel.AgreementStatus.Exhausted]: 3,
    [relationshipSummaryModel.AgreementStatus.Terminated]: 4
};
function sortByLastBilledDate(a, b) {
    if (!a.lastBilledDate && !b.lastBilledDate) return 0;
    if (!a.lastBilledDate) return 1;
    if (!b.lastBilledDate) return -1;
    return b.lastBilledDate.getTime() - a.lastBilledDate.getTime();
}
function sortByAgreementStatus(a, b) {
    const aStatus = relationshipSummaryModel.agreementStatus(a);
    const bStatus = relationshipSummaryModel.agreementStatus(b);
    const aOrder = aStatus == null ? AD_HOC_STATUS_SORT_ORDER : agreementStatusSortOrder[aStatus];
    const bOrder = bStatus == null ? AD_HOC_STATUS_SORT_ORDER : agreementStatusSortOrder[bStatus];
    return aOrder - bOrder;
}
function sortByPaymentMethod(a, b) {
    return getPaymentMethodOrder(a.fundingSource) - getPaymentMethodOrder(b.fundingSource);
}
function getPaymentMethodOrder(pm) {
    if (!pm) return 5;
    if (pm.isCreditCard()) return 4;
    if (pm.isVerified()) return 3;
    if (pm.isPendingMicroDeposit()) return 2;
    // failed bankAccount
    return 1;
}
export function sortAgreements(config) {
    const { agreements, sort, isVendor, headerItems } = config;
    const result = agreements.slice();
    if (sort) {
        var _headerItems_sort_colIndex;
        const colId = (_headerItems_sort_colIndex = headerItems[sort.colIndex]) === null || _headerItems_sort_colIndex === void 0 ? void 0 : _headerItems_sort_colIndex.id;
        if (colId) {
            result.sort(sortFunctions[colId]({
                isVendor,
                isAscending: sort.sort === 'asc'
            }));
        }
    }
    return result;
}
export const readAmendmentPaneOpenFromLocationState = (state)=>state != null && typeof state === 'object' && 'amendmentPaneOpen' in state && state.amendmentPaneOpen === true;
// WHY: a persisted scope round-trips through the agent as an opaque object, so its shape is re-checked here before it is allowed to drive the live agreements table.
export const isAgreementSearchFilterShape = (filter)=>filter != null && typeof filter === 'object' && !Array.isArray(filter) && 'version' in filter && filter.version === '1';
// WHY: a chip-edited filter has no faithful natural language — lastQuery describes only the pre-edit search, so keeping it would scope the amendment turn over agreements the chips no longer match.
export const deriveActiveFilterText = (lastQuery, filterEditedSinceLastSearch)=>filterEditedSinceLastSearch ? '' : lastQuery;
export const selectionWithinAgreements = (param)=>{
    let { selected, agreements } = param;
    const ids = agreements.map((agreement)=>agreement.id);
    return mapValues(selected, (isSelected, agreementId)=>ids.includes(agreementId) ? isSelected : false);
};
